import { Component, OnInit } from '@angular/core';
import { CanDisable, mixinDisabled } from '@angular/material/core';
import { Observable, filter, forkJoin, switchMap, take, tap } from 'rxjs';

import { AsyncPipe, NgIf } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyButtonModule } from '@angular/material/legacy-button';
import { MatLegacyTableModule } from '@angular/material/legacy-table';
import { MatLegacyTooltipModule } from '@angular/material/legacy-tooltip';
import { ComponentsModule } from '../../../components/components.module';
import { FastenerBasePointHelper } from '../../../helpers/fastener-base-point.helper';
import { CuttingCustomHole, FastenerBasePointEnum } from '../../../models/cutting/cutting-custom-hole.model';
import { CuttingFixedFastenersSet } from '../../../models/cutting/cutting-fixed-fasteners-set.model';
import { Position } from '../../../models/cutting/cutting-svg.model';
import { Countertop, CountertopFastenersSet, CountertopHole } from '../../../models/cutting/public-api';
import { LegacyDialogsService } from '../../../services/legacy-dialogs.service';
import {
  CountertopFastenersWizardDialogComponent,
  CountertopFastenersWizardDialogData,
} from '../../fasteners-wizard-dialog/countertop-fasteners-wizard-dialog.component';
import { FastenersWizardDialogResult } from '../../fasteners-wizard-dialog/fasteners-wizard-dialog.types';
import { CountertopSettingsDataService } from '../countertop-settings-data.service';
import { CountertopSettingsStateService } from '../countertop-settings-state.service';

export class FastenersBase {}

// eslint-disable-next-line @typescript-eslint/naming-convention
export const _FastenersMixinBase = mixinDisabled(FastenersBase);

@Component({
  selector: 'csl-fasteners',
  styleUrls: ['./fasteners.component.scss'],
  templateUrl: './fasteners.component.html',
  // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
  inputs: ['disabled'],
  standalone: true,
  imports: [
    NgIf,
    MatLegacyTableModule,
    ComponentsModule,
    MatLegacyButtonModule,
    MatLegacyTooltipModule,
    MatIconModule,
    AsyncPipe,
  ],
})
export class FastenersComponent extends _FastenersMixinBase implements OnInit, CanDisable {
  data$: Observable<any>;
  action$: Observable<any>;

  columns = ['name', 'base', 'coord', 'delete'];
  dataSource: (CuttingFixedFastenersSet | CuttingCustomHole)[] = [];

  constructor(
    private countertopSettingsService: CountertopSettingsStateService,
    private countertopDataService: CountertopSettingsDataService,
    private dialogs: LegacyDialogsService
  ) {
    super();
  }

  ngOnInit(): void {
    this.data$ = this.countertopSettingsService.countertop$.pipe(
      tap((p: Countertop) => (this.dataSource = [...p.outline.fastenersSets, ...p.outline.customHoles]))
    );
  }

  getBasePointAnglePosition(side: Position, basePoint: FastenerBasePointEnum): Position {
    return FastenerBasePointHelper.getBasePointAnglePosition(side, basePoint);
  }

  delete(item: CuttingFixedFastenersSet | CuttingCustomHole): void {
    this.action$ = this.countertopSettingsService.countertop$.pipe(
      take(1),
      tap((p: Countertop) => {
        const remove = (list: (CuttingFixedFastenersSet | CuttingCustomHole)[]): void => {
          const index = list.indexOf(item);
          if (index >= 0) {
            list.splice(index, 1);
          }
        };

        remove(p.outline.fastenersSets);
        remove(p.outline.customHoles);

        this.countertopSettingsService.updateCountertop(p);
      })
    );
  }

  addFastener(): void {
    let countertop: Countertop;
    this.action$ = forkJoin([
      this.countertopSettingsService.countertop$.pipe(
        take(1),
        tap(p => (countertop = p))
      ),
      this.countertopDataService.getAvailableHoleSchema(),
      this.countertopDataService.getAvailableFastenersSetSchema(),
    ]).pipe(
      switchMap(res =>
        this.dialogs.component<CountertopFastenersWizardDialogData, FastenersWizardDialogResult>(
          CountertopFastenersWizardDialogComponent,
          {
            countertop: countertop,
            mainMaterialThickness: res[0].material ? res[0].material.thickness : 0,
            holeSchemas: res[1],
            holeSetSchemas: res[2],
            defaultMirrorAxis: 'x',
          },
          1000
        )
      ),
      filter(res => !!res),
      tap(res => this.setFastenersToPanel(countertop, res))
    );
  }

  private setFastenersToPanel(countertop: Countertop, fasteners: FastenersWizardDialogResult): void {
    {
      const holes: CountertopHole[] = fasteners?.holes?.map(f => ({ position: f.side, ...f.item })) ?? [];
      countertop.outline.customHoles.push(...holes);
    }

    {
      const sets: CountertopFastenersSet[] = fasteners?.sets?.map(f => ({ position: f.side, ...f.item })) ?? [];
      countertop.outline.fastenersSets.push(...sets);
    }

    this.countertopSettingsService.updateCountertop(countertop);
  }
}
